import React, { useState } from 'react';
import { MoreVertical, Edit, Trash2, X } from 'lucide-react';
import { brandName, brandEmail, brandPhone } from '../lib/brand';

// Size upcharges (matches /app/backend/services/pricing.py upcharge_map).
// Update here AND in pricing.py if you change the upcharge schedule.
const SIZE_UPCHARGES = {
  '2XL': 1.25, '3XL': 2.50, '4XL': 3.75, '5XL': 5.00,
  '2X': 1.25, '3X': 2.50, '4X': 3.75, '5X': 5.00,
  // Normalised lowercase keys handled in priceForSize() below.
};

const PRINT_METHOD_LABELS = {
  screen_print: 'Screen Printing',
  embroidery: 'Embroidery',
  dtf: 'DTF',
  dtg: 'DTG',
  sublimation: 'Sublimation',
  vinyl: 'Heat Transfer Vinyl',
};

// Used by both the admin proposal page and the client payment page to
// auto-group items by decoration method so production can clearly see
// "all 3 screen-print items, then all 2 embroidery items".
const CPP_LABEL = 'Customer Supplied';
const UNKNOWN_LABEL = 'Other Items';

// Youth garments (Sport-Tek YST350, Port & Co PC54Y, etc.) use Y-prefixed
// sizes (YXS, YS, YM, YL, YXL). When the catalog stores them as plain
// S/M/L the customer screen still needs to render them with the Y prefix.
const YOUTH_SIZE_BASES = new Set(['XS', 'S', 'M', 'L', 'XL']);

/**
 * Detect youth garments from any of the usual data shape signals.
 * Style codes (YST350, PC54Y, YL500), the styleName ("Youth ..."), or an
 * explicit is_youth flag.
 */
export const isYouthItem = (item) => {
  if (!item) return false;
  if (item.is_youth === true) return true;
  const style = String(item.style || item.styleID || item.style_number || '').toUpperCase();
  const name = String(item.styleName || item.description || '').toLowerCase();
  if (style.startsWith('Y')) return true;
  if (style.endsWith('Y')) return true; // PC54Y et al.
  if (name.includes('youth')) return true;
  return false;
};

/**
 * Render the human-readable size label. For youth garments we ensure every
 * base size (S/M/L/XL/XS) gets the Y prefix so the customer doesn't see
 * "M, L, YS" as a confusing mix.
 */
export const displaySize = (item, sizeKey) => {
  const raw = String(sizeKey || '').trim().toUpperCase();
  if (!raw) return raw;
  if (!isYouthItem(item)) return raw;
  if (raw.startsWith('Y')) return raw;
  if (YOUTH_SIZE_BASES.has(raw)) return `Y${raw}`;
  return raw;
};

export const methodLabelFor = (item) => {
  if (item?.is_customer_supplied || item?.cpp || item?.product_type === 'cpp') {
    return CPP_LABEL;
  }
  const m = String(item?.print_method || '').toLowerCase().trim();
  if (!m) return UNKNOWN_LABEL;
  return PRINT_METHOD_LABELS[m] || m.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// Preserve a meaningful order: most-common production methods first,
// then anything else alphabetically, with Customer Supplied at the end.
const METHOD_ORDER = ['Screen Printing', 'Embroidery', 'DTF', 'DTG', 'Sublimation', 'Heat Transfer Vinyl'];
const methodOrderIndex = (label) => {
  if (label === CPP_LABEL) return 1000;
  if (label === UNKNOWN_LABEL) return 999;
  const i = METHOD_ORDER.indexOf(label);
  return i === -1 ? 500 : i;
};

/**
 * Group an items array by decoration method. Returns an ordered array of
 * `{ label, items, totalPieces, totalAmount }` so callers can render section
 * headers + totals in one pass.
 */
export const groupItemsByMethod = (items) => {
  const buckets = new Map();
  (items || []).forEach((it, idx) => {
    const label = methodLabelFor(it);
    if (!buckets.has(label)) buckets.set(label, []);
    buckets.get(label).push({ ...it, _origIndex: idx });
  });
  return Array.from(buckets.entries())
    .map(([label, group]) => {
      const totalPieces = group.reduce((s, it) => {
        const sz = it.sizes || {};
        const fromSizes = Object.values(sz).reduce((a, v) => a + (Number(v) || 0), 0);
        return s + (fromSizes || Number(it.quantity || 0));
      }, 0);
      const totalAmount = group.reduce((s, it) => {
        // CPP items contribute nothing to the section total — the customer
        // brought the garment and we're not billing for it. Decoration is
        // still in the section header count via the proposal grand total.
        const isCPPItem = !!(it.is_cpp || it.is_customer_supplied || it.cpp || it.product_type === 'cpp');
        if (isCPPItem) return s;
        if (Number(it.total)) return s + Number(it.total);
        const base = Number(it.markup_price) || Number(it.garment_cost) || 0;
        return s + base * (Number(it.quantity) || 0);
      }, 0);
      return { label, items: group, totalPieces, totalAmount };
    })
    .sort((a, b) => methodOrderIndex(a.label) - methodOrderIndex(b.label));
};


const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '2X', '3X', '4X', '5X'];

const priceForSize = (size, basePrice, overrides) => {
  // Admin per-size override always wins. Stored as item.size_price_overrides
  // e.g. { S: 10, M: 10, L: 10, XL: 11, "2XL": 12.50 }
  if (overrides && typeof overrides === 'object') {
    const v = overrides[String(size).toUpperCase()] ?? overrides[size];
    if (v !== undefined && v !== null && v !== '') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  const key = String(size || '').toUpperCase();
  const upcharge = SIZE_UPCHARGES[key] || 0;
  return (Number(basePrice) || 0) + upcharge;
};

const estimateSetupFee = (item) => {
  // Admin override (stored as item.setup_fee) always wins.
  const override = Number(item.setup_fee);
  if (Number.isFinite(override) && override >= 0 && item.setup_fee !== undefined && item.setup_fee !== null && item.setup_fee !== '') {
    return override;
  }
  // Otherwise estimate from the print spec. Defaults match the engine:
  // screen print = $25/color/location; embroidery = $40 digitizing flat;
  // DTF/DTG/sublimation = no setup.
  const method = String(item.print_method || 'screen_print').toLowerCase();
  // print_colors can be a number, numeric string, OR a dict like
  // { front: 2, back: 1 } on legacy items. Sum dict values; coerce strings.
  let colors = 0;
  const raw = item.print_colors;
  if (typeof raw === 'number') {
    colors = raw;
  } else if (typeof raw === 'string') {
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) colors = n;
  } else if (raw && typeof raw === 'object') {
    colors = Object.values(raw).reduce((s, v) => s + (Number(v) || 0), 0);
  }
  if (!Number.isFinite(colors) || colors < 0) colors = 0;

  let locs = Number(item.location_count);
  if (!Number.isFinite(locs) || locs <= 0) {
    locs = Array.isArray(item.print_locations) ? item.print_locations.length : 0;
  }
  if (!Number.isFinite(locs) || locs <= 0) locs = 1;

  let fee = 0;
  if (method === 'embroidery') fee = 40;
  else if (method === 'dtf' || method === 'dtg' || method === 'sublimation') fee = 0;
  else fee = 25 * colors * locs;
  return Number.isFinite(fee) && fee >= 0 ? fee : 0;
};

/**
 * Polished item card. Used on both the admin proposal view and the public
 * client view — `isAdmin` controls whether the Setup Fee row is shown.
 *
 * Props:
 *   item         the proposal item dict
 *   isAdmin      bool — show the "Estimated Art / Setup Fee" header
 *   onEdit       () => void — admin action (Edit button in kebab menu)
 *   onRemove     () => void — admin action (Remove button in kebab menu)
 *   onEditNotes  () => void — opens the existing notes editor
 *   mockupSrc    optional override; if not provided, picked from item.mockup_views
 */
export const ProductDisplayCard = ({
  item,
  isAdmin = false,
  allInPricing = false,
  onEdit,
  onRemove,
  onEditNotes,
  onSetupFeeChange,
  onColorsChange,
  onLocationsChange,
  onSizePriceChange,
  onPrintPriceChange,
  mockupSrc,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(false);
  const [feeDraft, setFeeDraft] = useState('');
  const [savingFee, setSavingFee] = useState(false);
  const [editingColors, setEditingColors] = useState(false);
  const [colorsDraft, setColorsDraft] = useState('');
  const [editingSize, setEditingSize] = useState(null);
  const [sizeDraft, setSizeDraft] = useState('');
  const [editingPrint, setEditingPrint] = useState(false);
  const [printDraft, setPrintDraft] = useState('');
  // CPP (Customer Provided Product) — admin marked this item as a garment
  // the customer is bringing. We zero the garment price line, hide per-size
  // $ amounts, and replace the item subtotal with a "Customer Supplied"
  // notice. Decoration cost (print/embroidery) still applies because we're
  // still doing the work.
  const isCPP = !!(item.is_cpp || item.is_customer_supplied || item.cpp || item.product_type === 'cpp');
  const basePrice = isCPP ? 0 : (Number(item.markup_price) || Number(item.garment_cost) || 0);

  const sizeEntries = Object.entries(item.sizes || {})
    .filter(([, v]) => Number(v || 0) > 0)
    .sort(([a], [b]) => {
      const ia = SIZE_ORDER.indexOf(String(a).toUpperCase());
      const ib = SIZE_ORDER.indexOf(String(b).toUpperCase());
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });

  const totalQty = sizeEntries.reduce((s, [, q]) => s + Number(q || 0), 0)
    || Number(item.quantity || 0);
  // Fall back to flat qty pricing ONLY when there are no size rows — a
  // computed $0 is legitimate (CPP items with $0 per-size overrides).
  const lineTotalGarment = sizeEntries.length > 0
    ? sizeEntries.reduce(
        (s, [size, q]) => s + priceForSize(size, basePrice, item.size_price_overrides) * Number(q || 0),
        0,
      )
    : basePrice * Number(item.quantity || 0);
  const setupFee = estimateSetupFee(item);
  // On the client-facing view we roll the print cost AND setup fee into the
  // line total so "$X each × Qty" matches the customer's expected math
  // against the grand total. On the admin view garments, print, and setup
  // are split for clarity.
  const printPerUnit = Number(item.print_cost || 0);
  const setupAllocPerUnit = allInPricing && totalQty > 0 ? setupFee / totalQty : 0;
  const lineTotal = allInPricing
    ? lineTotalGarment + printPerUnit * totalQty + setupFee
    : lineTotalGarment;
  const perItem = totalQty > 0 ? lineTotal / totalQty : (basePrice + (allInPricing ? printPerUnit : 0));

  const methodLabel = PRINT_METHOD_LABELS[String(item.print_method || '').toLowerCase()]
    || (item.print_method ? String(item.print_method).replace(/_/g, ' ') : 'Screen Printing');
  const locations = Array.isArray(item.print_locations) ? item.print_locations : [];
  // Same defensive coercion as estimateSetupFee — colorsCount might be a
  // dict on legacy items so we sum it.
  let colorsCount = 0;
  {
    const raw = item.print_colors;
    if (typeof raw === 'number') colorsCount = raw;
    else if (typeof raw === 'string') {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) colorsCount = n;
    } else if (raw && typeof raw === 'object') {
      colorsCount = Object.values(raw).reduce((s, v) => s + (Number(v) || 0), 0);
    }
    if (!Number.isFinite(colorsCount) || colorsCount < 0) colorsCount = 0;
  }
  const colorNames = Array.isArray(item.print_color_names) ? item.print_color_names : [];

  const mockup = mockupSrc
    || item.mockup_views?.front
    || item.mockup_views?.back
    || item.mockup_url
    || item.design_file
    || null;

  const productTitle = String(
    item.product_name || item.style || item.styleID || 'Product',
  ).toUpperCase();
  const brandSubtitle = [item.brand, item.style_number || item.styleID]
    .filter(Boolean).join(' | ');

  return (
    <div
      data-testid="product-display-card"
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {/* Admin-only: Estimated Art header with Setup Fee + print spec columns */}
      {isAdmin && (
        <div
          data-testid="estimated-art-block"
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            background: '#fff',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#0f172a' }}>
              Estimated Art
              {isCPP && (
                <span style={{ marginLeft: 10, fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '2px 8px', borderRadius: 999, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  Customer Supplied
                </span>
              )}
            </h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {/* For CPP items the shop did NOT sell the garment — hide the
                  Shirt + All-in $/ea rows so admins (and clients seeing this
                  card) aren't confused into thinking we're charging for it. */}
              {!isCPP && (<>
              <div style={{ fontSize: 15, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Shirt:</span>
                <span
                  data-testid="estart-shirt-cost"
                  style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}
                >
                  ${Number(basePrice || 0).toFixed(2)}/ea
                </span>
              </div>
              {/* All-in per piece (garment + print). Quick sanity number admins
                  asked for so they don't have to mentally sum Shirt + Print. */}
              <div style={{ fontSize: 15, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Total/ea:</span>
                <span
                  data-testid="estart-allin-cost"
                  title={`Shirt $${Number(basePrice || 0).toFixed(2)} + Print $${Number(item.print_cost || 0).toFixed(2)}`}
                  style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}
                >
                  ${(Number(basePrice || 0) + Number(item.print_cost || 0)).toFixed(2)}/ea
                </span>
              </div>
              </>)}
              {/* Print Price (per piece) — InkSoft-style override. When set,
                  locks this item's print cost so matrix re-pricing won't
                  silently overwrite it. */}
              <div style={{ fontSize: 15, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Print:</span>
                {editingPrint ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={printDraft}
                      autoFocus
                      onChange={(e) => setPrintDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          document.querySelector('[data-testid="save-print-price"]')?.click();
                        } else if (e.key === 'Escape') {
                          setEditingPrint(false);
                        }
                      }}
                      data-testid="print-price-input"
                      style={{ width: 80, padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontWeight: 600, color: '#0f172a' }}
                    />
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>/ea</span>
                    <button
                      onClick={async () => {
                        if (!onPrintPriceChange) { setEditingPrint(false); return; }
                        const val = parseFloat(printDraft);
                        if (!Number.isFinite(val) || val < 0) return;
                        await onPrintPriceChange(val);
                        setEditingPrint(false);
                      }}
                      data-testid="save-print-price"
                      style={{ padding: '4px 10px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingPrint(false)}
                      data-testid="cancel-print-price"
                      style={{ padding: '4px 10px', background: 'transparent', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    onClick={() => {
                      if (!onPrintPriceChange) return;
                      setPrintDraft(Number(item.print_cost || 0).toFixed(2));
                      setEditingPrint(true);
                    }}
                    data-testid="edit-print-price-trigger"
                    disabled={!onPrintPriceChange}
                    title={onPrintPriceChange ? 'Click to override print price (disables matrix auto-pricing for this item)' : ''}
                    style={{
                      background: 'transparent', border: 'none',
                      cursor: onPrintPriceChange ? 'pointer' : 'default',
                      padding: '2px 6px', borderRadius: 6, fontSize: 15, fontWeight: 700, color: '#0f172a',
                      borderBottom: onPrintPriceChange ? '1px dashed #94a3b8' : 'none',
                    }}
                  >
                    ${Number(item.print_cost || 0).toFixed(2)}/ea
                    {item.print_cost_locked && (
                      <span style={{ fontSize: 10, color: '#0ea5e9', marginLeft: 6, fontWeight: 700, letterSpacing: 0.5 }}>
                        OVERRIDE
                      </span>
                    )}
                  </button>
                )}
              </div>
              {/* Setup Fee (existing) */}
              <div style={{ fontSize: 15, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>Setup Fee:</span>
                {editingFee ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: '#0f172a' }}>$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feeDraft}
                    autoFocus
                    onChange={(e) => setFeeDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        document.querySelector('[data-testid="save-setup-fee"]')?.click();
                      } else if (e.key === 'Escape') {
                        setEditingFee(false);
                      }
                    }}
                    data-testid="setup-fee-input"
                    style={{
                      width: 90, padding: '4px 8px',
                      border: '1px solid #cbd5e1', borderRadius: 6,
                      fontSize: 15, fontWeight: 600, color: '#0f172a',
                    }}
                  />
                  <button
                    onClick={async () => {
                      if (!onSetupFeeChange) { setEditingFee(false); return; }
                      const val = Number(feeDraft);
                      if (!Number.isFinite(val) || val < 0) { return; }
                      setSavingFee(true);
                      try {
                        await onSetupFeeChange(val);
                        setEditingFee(false);
                      } finally {
                        setSavingFee(false);
                      }
                    }}
                    disabled={savingFee}
                    data-testid="save-setup-fee"
                    style={{
                      padding: '4px 10px', background: '#0ea5e9', color: '#fff',
                      border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {savingFee ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditingFee(false)}
                    data-testid="cancel-setup-fee"
                    style={{
                      padding: '4px 10px', background: 'transparent', color: '#64748b',
                      border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  onClick={() => {
                    if (!onSetupFeeChange) return;
                    setFeeDraft(setupFee.toFixed(2));
                    setEditingFee(true);
                  }}
                  data-testid="edit-setup-fee-trigger"
                  title={onSetupFeeChange ? 'Click to override setup fee for this item' : ''}
                  style={{
                    background: 'transparent', border: 'none',
                    cursor: onSetupFeeChange ? 'pointer' : 'default', padding: '2px 6px',
                    borderRadius: 6, fontSize: 15, fontWeight: 700, color: '#0f172a',
                    borderBottom: onSetupFeeChange ? '1px dashed #94a3b8' : 'none',
                  }}
                >
                  ${setupFee.toFixed(2)}
                  {item.setup_fee !== undefined && item.setup_fee !== null && item.setup_fee !== '' && (
                    <span style={{ fontSize: 10, color: '#0ea5e9', marginLeft: 6, fontWeight: 700, letterSpacing: 0.5 }}>
                      OVERRIDE
                    </span>
                  )}
                </button>
              )}
            </div>
            </div>
          </div>
          {/* Locations + per-location colors. Falls back to a synthesized
              "Front" row when the item has no print_locations array (legacy
              imports, AI-created items without an explicit array) so the
              admin can still click to edit colors. */}
          {(() => {
            const renderLocations = locations.length > 0 ? locations : ['front'];
            const canRemoveLoc = !!onLocationsChange && locations.length > 1;
            // Standard print location options. Filter out those already used
            // (case-insensitive) so the +Add menu only offers fresh spots.
            const LOCATION_OPTIONS = ['Front', 'Back', 'Left Sleeve', 'Right Sleeve', 'Left Chest', 'Right Chest', 'Hood', 'Pocket'];
            const usedLower = new Set(locations.map((l) => String(l).toLowerCase()));
            const availableToAdd = LOCATION_OPTIONS.filter((l) => !usedLower.has(l.toLowerCase()));
            return (
            <div style={{ display: 'flex', gap: 40, marginTop: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {renderLocations.map((loc, idx) => (
                <div key={idx} style={{ fontSize: 14, color: '#475569' }}>
                  <div style={{ fontWeight: 600, color: '#0f172a', textTransform: 'capitalize', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{loc}</span>
                    {canRemoveLoc && (
                      <button
                        onClick={() => onLocationsChange(locations.filter((_, i) => i !== idx), loc)}
                        data-testid={`remove-location-${String(loc).toLowerCase()}`}
                        title={`Remove ${loc} print`}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          padding: 2, borderRadius: 4, color: '#94a3b8',
                          display: 'inline-flex', alignItems: 'center',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'transparent'; }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                  {editingColors ? (
                    // Only render the editor inputs once (under the first
                    // location) — colors are a single per-item field, not
                    // per-location, so a single editor is correct.
                    idx === 0 ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <input
                        type="number"
                        step="1"
                        min="0"
                        max="12"
                        value={colorsDraft}
                        autoFocus
                        onChange={(e) => setColorsDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            document.querySelector('[data-testid="save-colors"]')?.click();
                          } else if (e.key === 'Escape') {
                            setEditingColors(false);
                          }
                        }}
                        data-testid="colors-input"
                        style={{
                          width: 60, padding: '4px 6px',
                          border: '1px solid #cbd5e1', borderRadius: 6,
                          fontSize: 14, fontWeight: 600, color: '#0f172a',
                        }}
                      />
                      <span>Color{Number(colorsDraft) === 1 ? '' : 's'}</span>
                      <button
                        onClick={async () => {
                          if (!onColorsChange) { setEditingColors(false); return; }
                          const val = Math.max(0, parseInt(colorsDraft, 10) || 0);
                          await onColorsChange(val);
                          setEditingColors(false);
                        }}
                        data-testid="save-colors"
                        style={{
                          padding: '3px 8px', background: '#0ea5e9', color: '#fff',
                          border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        Re-price
                      </button>
                      <button
                        onClick={() => setEditingColors(false)}
                        data-testid="cancel-colors"
                        style={{
                          padding: '3px 8px', background: 'transparent', color: '#64748b',
                          border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12,
                          cursor: 'pointer',
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    ) : (
                      <div style={{ fontStyle: 'italic', color: '#94a3b8', fontSize: 13 }}>
                        editing…
                      </div>
                    )
                  ) : (
                    <button
                      onClick={() => {
                        if (!onColorsChange) return;
                        setColorsDraft(String(Number.isFinite(colorsCount) ? colorsCount : 1));
                        setEditingColors(true);
                      }}
                      data-testid={`edit-colors-trigger-${idx}`}
                      disabled={!onColorsChange}
                      title={onColorsChange ? 'Click to edit colors and re-price from matrix' : ''}
                      style={{
                        background: 'transparent', border: 'none',
                        cursor: onColorsChange ? 'pointer' : 'default',
                        padding: '2px 4px', fontSize: 14, color: '#475569',
                        borderBottom: onColorsChange ? '1px dashed #94a3b8' : 'none',
                      }}
                    >
                      {colorsCount} Color{colorsCount === 1 ? '' : 's'}
                    </button>
                  )}
                </div>
              ))}
              {/* +Add Location — admin-only, hidden when all standard
                  options are already in use. Uses a native <select> so it
                  works without extra portal/dropdown deps. */}
              {!!onLocationsChange && availableToAdd.length > 0 && (
                <div style={{ fontSize: 14 }}>
                  <select
                    data-testid="add-location-select"
                    defaultValue=""
                    onChange={(e) => {
                      const val = e.target.value;
                      if (!val) return;
                      onLocationsChange([...locations, val], null);
                      // Reset to placeholder so the same option could be reselected later
                      e.target.value = "";
                    }}
                    style={{
                      padding: '6px 10px',
                      border: '1px dashed #94a3b8',
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#0ea5e9',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">+ Add Location</option>
                    {availableToAdd.map((l) => (
                      <option key={l} value={l}>{l}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            );
          })()}
        </div>
      )}

      {/* Product header: title + brand subtitle + kebab menu */}
      <div style={{ padding: '20px 20px 0', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2
              style={{
                fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a',
                textTransform: 'uppercase', letterSpacing: 0.2, lineHeight: 1.2,
              }}
              data-testid="product-title"
            >
              {productTitle}
            </h2>
            {brandSubtitle && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {brandSubtitle}
              </div>
            )}
          </div>
          {isAdmin && (onEdit || onRemove) && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                data-testid="card-menu-button"
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 4, borderRadius: 6, color: '#64748b',
                }}
                aria-label="Item menu"
              >
                <MoreVertical size={20} />
              </button>
              {menuOpen && (
                <div
                  data-testid="card-menu-dropdown"
                  style={{
                    position: 'absolute', right: 0, top: '100%', marginTop: 4,
                    background: '#fff', border: '1px solid #e2e8f0',
                    borderRadius: 8, boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                    minWidth: 140, zIndex: 10, overflow: 'hidden',
                  }}
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  {onEdit && (
                    <button
                      onClick={() => { setMenuOpen(false); onEdit(); }}
                      data-testid="card-menu-edit"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', padding: '10px 14px', background: 'transparent',
                        border: 'none', cursor: 'pointer', fontSize: 14, color: '#0f172a',
                        textAlign: 'left',
                      }}
                    >
                      <Edit size={14} /> Edit item
                    </button>
                  )}
                  {onRemove && (
                    <button
                      onClick={() => { setMenuOpen(false); onRemove(); }}
                      data-testid="card-menu-remove"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        width: '100%', padding: '10px 14px', background: 'transparent',
                        border: 'none', cursor: 'pointer', fontSize: 14, color: '#dc2626',
                        textAlign: 'left',
                      }}
                    >
                      <Trash2 size={14} /> Remove item
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Product image */}
        {mockup && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
            <img
              src={mockup}
              alt={productTitle}
              style={{
                maxWidth: 220, maxHeight: 220, objectFit: 'contain',
                background: '#fff',
              }}
              data-testid="product-image"
            />
          </div>
        )}

        {/* Style: COLOR row */}
        <div style={{ display: 'flex', gap: 24, marginTop: mockup ? 0 : 12, marginBottom: 12 }}>
          <div style={{ minWidth: 80, color: '#64748b', fontSize: 15 }}>Style:</div>
          <div style={{ color: '#0f172a', fontSize: 15, fontWeight: 600, textTransform: 'uppercase' }}>
            {item.color || '—'}
          </div>
        </div>

        {/* Per-size price rows */}
        {sizeEntries.length > 0 && (
          <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
            <div style={{ minWidth: 80, color: '#64748b', fontSize: 15 }}>Size:</div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: isCPP ? 'auto auto' : 'auto auto auto auto', columnGap: 12, rowGap: 6, alignItems: 'baseline' }}>
              {sizeEntries.map(([size, qty]) => {
                if (isCPP) {
                  // CPP: render size + qty only, no price columns.
                  return (
                    <React.Fragment key={size}>
                      <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600 }}>
                        {displaySize(item, size)}
                      </div>
                      <div style={{ fontSize: 15, color: '#64748b' }}>({qty}) — customer supplied</div>
                    </React.Fragment>
                  );
                }
                const price = priceForSize(size, basePrice, item.size_price_overrides)
                  + (allInPricing ? printPerUnit + setupAllocPerUnit : 0);
                const overrides = item.size_price_overrides || {};
                const hasOverride = (overrides[String(size).toUpperCase()] !== undefined && overrides[String(size).toUpperCase()] !== null)
                  || (overrides[size] !== undefined && overrides[size] !== null);
                const isEditing = editingSize === String(size).toUpperCase();
                return (
                  <React.Fragment key={size}>
                    <div style={{ fontSize: 15, color: '#0f172a', fontWeight: 600 }}>
                      {displaySize(item, size)}
                    </div>
                    <div style={{ fontSize: 15, color: '#64748b' }}>({qty})</div>
                    <div style={{ fontSize: 15, color: '#94a3b8' }}>×</div>
                    {isEditing ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 14, color: '#0f172a' }}>$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={sizeDraft}
                          autoFocus
                          onChange={(e) => setSizeDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              document.querySelector(`[data-testid="save-size-price-${size.toUpperCase()}"]`)?.click();
                            } else if (e.key === 'Escape') {
                              setEditingSize(null);
                            }
                          }}
                          data-testid={`size-price-input-${size.toUpperCase()}`}
                          style={{
                            width: 70, padding: '3px 6px',
                            border: '1px solid #cbd5e1', borderRadius: 6,
                            fontSize: 14, color: '#0f172a',
                          }}
                        />
                        <button
                          onClick={async () => {
                            if (!onSizePriceChange) { setEditingSize(null); return; }
                            const v = sizeDraft.trim() === '' ? null : parseFloat(sizeDraft);
                            if (v !== null && (!Number.isFinite(v) || v < 0)) return;
                            await onSizePriceChange(String(size).toUpperCase(), v);
                            setEditingSize(null);
                          }}
                          data-testid={`save-size-price-${size.toUpperCase()}`}
                          style={{
                            padding: '3px 8px', background: '#0ea5e9', color: '#fff',
                            border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingSize(null)}
                          data-testid={`cancel-size-price-${size.toUpperCase()}`}
                          style={{
                            padding: '3px 8px', background: 'transparent', color: '#64748b',
                            border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12,
                            cursor: 'pointer',
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          if (!onSizePriceChange || !isAdmin) return;
                          setSizeDraft(price.toFixed(2));
                          setEditingSize(String(size).toUpperCase());
                        }}
                        data-testid={`size-price-cell-${size.toUpperCase()}`}
                        disabled={!onSizePriceChange || !isAdmin}
                        title={onSizePriceChange && isAdmin ? 'Click to override this size\'s price' : ''}
                        style={{
                          background: 'transparent', border: 'none', padding: '2px 4px',
                          textAlign: 'left',
                          cursor: onSizePriceChange && isAdmin ? 'pointer' : 'default',
                          fontSize: 15, color: hasOverride ? '#0c4a6e' : '#0f172a',
                          fontWeight: hasOverride ? 700 : 400,
                          borderBottom: onSizePriceChange && isAdmin ? '1px dashed #94a3b8' : 'none',
                        }}
                      >
                        ${price.toFixed(2)}/each
                        {hasOverride && (
                          <span style={{ fontSize: 9, color: '#0ea5e9', marginLeft: 5, fontWeight: 700, letterSpacing: 0.5 }}>
                            ★
                          </span>
                        )}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Production spec callout */}
      <div style={{ padding: '0 20px 16px' }}>
        <div
          data-testid="production-spec-box"
          style={{
            background: '#f1f5f9',
            borderRadius: 8,
            padding: '14px 16px',
            fontSize: 14,
            color: '#0f172a',
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{methodLabel}</div>
          {locations.length > 0 ? (
            locations.map((loc, idx) => {
              const colorName = colorNames[idx] || colorNames[0] || '';
              // Per-location print/logo dimensions admin sets via the
              // mockup card. Read-only here; shown to both admin and
              // customer so what's approved matches what gets produced.
              const printSizes = item.print_sizes || {};
              const sizeText = printSizes[String(loc).toLowerCase()] || printSizes[loc] || '';
              return (
                <div key={idx} style={{ marginBottom: idx < locations.length - 1 ? 8 : 0 }}>
                  <strong style={{ textTransform: 'capitalize' }}>{colorName ? `${colorName} ` : ''}{loc} print</strong>
                  {sizeText && (
                    <span style={{ color: '#475569', fontWeight: 500 }}> · Logo size: <span style={{ color: '#0f172a', fontWeight: 600 }}>{sizeText}</span></span>
                  )}
                </div>
              );
            })
          ) : null}
          {/* Production / decoration notes — shown to BOTH admin and customer
              so the customer can verify what they're ordering ("front print
              only, white ink", "no back print", etc.). Set via the per-item
              Notes Log in the proposal view. */}
          {item.decoration_notes && (
            <div
              data-testid="production-notes-public"
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', color: '#475569', marginBottom: 4 }}>
                Production Notes
              </div>
              <div style={{ whiteSpace: 'pre-wrap', color: '#0f172a' }}>
                {item.decoration_notes}
              </div>
            </div>
          )}
        </div>
        {isAdmin && onEditNotes && (
          <button
            onClick={onEditNotes}
            data-testid="edit-notes-link"
            style={{
              marginTop: 10, background: 'transparent', border: 'none',
              color: '#0ea5e9', fontSize: 14, fontWeight: 500, cursor: 'pointer',
              padding: 0,
            }}
          >
            Edit notes
          </button>
        )}
      </div>

      {/* Item subtotal footer */}
      <div
        data-testid="item-subtotal-footer"
        style={{
          background: isCPP ? '#fef3c7' : '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          padding: '16px 20px',
          textAlign: 'center',
        }}
      >
        {isCPP ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#92400e' }} data-testid="cpp-no-price-notice">
              {totalQty} Customer-Supplied Item{totalQty === 1 ? '' : 's'}
            </div>
            <div style={{ fontSize: 13, color: '#78350f', marginTop: 4 }}>
              No garment charge — {brandName()} is decorating only.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>
            {totalQty} Items / <span data-testid="item-line-total">${lineTotal.toFixed(2)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDisplayCard;

/**
 * Section header rendered above a group of items that share a decoration
 * method. Used by both the admin proposal page and the client payment page.
 */
export const MethodSectionHeader = ({ label, totalPieces, itemCount, totalAmount, showAmount = true }) => (
  <div
    data-testid={`method-section-${String(label).toLowerCase().replace(/\s+/g, '-')}`}
    style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 12,
      padding: '14px 16px',
      background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)',
      color: '#fff',
      borderRadius: 10,
      marginBottom: 10,
      flexWrap: 'wrap',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: 1.5,
        textTransform: 'uppercase', color: '#94a3b8',
      }}>
        Decoration Method
      </span>
      <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{label}</span>
    </div>
    <div style={{ fontSize: 13, color: '#cbd5e1' }}>
      {itemCount} item{itemCount === 1 ? '' : 's'} · {totalPieces} piece{totalPieces === 1 ? '' : 's'}
      {showAmount && totalAmount > 0 && (
        <> · <strong style={{ color: '#fff' }}>${Number(totalAmount || 0).toFixed(2)}</strong></>
      )}
    </div>
  </div>
);
